import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Send, X, StopCircle, ChevronUp, ChevronDown, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { resolveToEnglishBookName } from '@/utils/bibleData';
import BibleReader from '@/components/BibleReader';
import Header from '@/components/Header';
import SettingsPage from '@/pages/SettingsPage';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { MessageLimitBanner, UpgradePrompt } from '@/components/UpgradePrompt';
import { BroadcastBanner, BroadcastChatMessages } from '@/components/BroadcastInline';
import PremiumHighlight from '@/components/PremiumHighlight';
import ShareModal from '@/components/ShareModal';
import ShareToFriendModal from '@/components/ShareToFriendModal';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';
import PostToFeedModal from '@/components/PostToFeedModal';
import TutorialOverlay from '@/components/TutorialOverlay';
import ConversationSidebar from '@/components/ConversationSidebar';
import ScripturePopup from '@/components/ScripturePopup';
import VaultPinModal from '@/components/VaultPinModal';
import RecapScheduleModal from '@/components/RecapScheduleModal';
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { subscribeToPush } from '@/services/pushNotifications';
import { API_URL as API } from '@/utils/apiConfig';
import { getDeviceFingerprint } from '@/utils/fingerprint';

// Extracted sub-components
import ChatMessageBubble from '@/pages/chat/ChatMessageBubble';
import ChatToolbar from '@/pages/chat/ChatToolbar';
import StarterQuestions from '@/pages/chat/StarterQuestions';
import SourcesDrawer from '@/pages/chat/SourcesDrawer';
import useVault from '@/pages/chat/useVault';

export default function ChatPage({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const [creatingConversation, setCreatingConversation] = useState(false);
  const scrollToMessageRef = useRef(null);
  const [guestPrompt, setGuestPrompt] = useState(null);
  const isGuest = !!user?.is_guest;
  const [fileUploading, setFileUploading] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeAnalyzing, setYoutubeAnalyzing] = useState(false);
  const [youtubeDialogOpen, setYoutubeDialogOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [showBibleReader, setShowBibleReader] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [userSettings, setUserSettings] = useState(null);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const isProcessing = useRef(false);
  const abortControllerRef = useRef(null);
  // Tracks whether an SSE stream was interrupted by the client going to
  // background (iOS/Android suspend the fetch reader). When the user
  // returns, we refetch the conversation from the DB — because the backend
  // keeps generating server-side, the full reply is already saved.
  const streamInterruptedRef = useRef(false);
  // True between the moment we suspect a background-interrupted stream and
  // the moment we've either recovered the reply from the DB or given up
  // and shown a retry. Keeps the "thinking" indicator visible during the
  // recovery poll so the user never sees a blank chat.
  const recoveringStreamRef = useRef(false);
  const recoveryPollTimerRef = useRef(null);
  // Holds the config for an active recovery poll so any listener
  // (e.g. visibility→visible) can re-trigger the next check without
  // losing context about which message we're waiting on.
  const recoveryPollConfigRef = useRef(null);

  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [bibleTarget, setBibleTarget] = useState(null);
  const [scripturePopupRef, setScripturePopupRef] = useState(null);
  const [showRecapSchedule, setShowRecapSchedule] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(null);
  const [shareModalData, setShareModalData] = useState(null);
  const [shareToFriendData, setShareToFriendData] = useState(null);
  const [postToFeedData, setPostToFeedData] = useState(null);
  // Feature-flag gate — hides "Share with Friend" / "Post to Feed" on
  // chat messages when the community_share flag (or its parent community
  // flag) is OFF. Cascades server-side.
  const { enabled: communityShareEnabled } = useFeatureFlag('community_share');
  const [showTutorial, setShowTutorial] = useState(true);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const discussScriptureRef = useRef(location.state?.discussScripture || null);
  const seedMessageRef = useRef(location.state?.seedMessage || null);
  const suggestedQuestionsRef = useRef(location.state?.suggestedQuestions || null);
  const autoSendSeedRef = useRef(!!location.state?.autoSend);
  // Holds a message that should be PRE-FILLED into the chat input the
  // moment `currentConversation` commits — used by "Discuss with Eili"
  // from Did You Know so the user lands in a fresh chat with the
  // topic ready to edit, but nothing is auto-sent. They decide what to
  // ask.
  const pendingPrefillRef = useRef(null);
  // Suggested questions chips shown above the input when arriving via
  // Did You Know. Cleared after the user sends a message or dismisses.
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);

  // Sources drawer state
  const [messageSources, setMessageSources] = useState({}); // { message_id: [sources] }
  const [sourcesDrawerOpen, setSourcesDrawerOpen] = useState(false);
  const [drawerSources, setDrawerSources] = useState([]);
  const [drawerActiveIndex, setDrawerActiveIndex] = useState(0);

  // Re-mount tutorial overlay when "Replay Tour" is triggered from settings
  useEffect(() => {
    const handleReplay = () => {
      // Small delay to ensure server-side reset completes before TutorialOverlay fetches
      setTimeout(() => setShowTutorial(true), 200);
    };
    window.addEventListener('replay-tutorial', handleReplay);
    return () => window.removeEventListener('replay-tutorial', handleReplay);
  }, []);

  // Feedback & Bookmarks
  const [feedbackMap, setFeedbackMap] = useState({}); // { message_id: 'up'|'down' }
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  // Lookup of bookmark organisation per message_id so we can render the
  // tiny "Group · Label" chip row right under the bookmark icon without
  // re-querying. Shape: { [message_id]: { bookmark_id, group_id, group_name, label_ids, labels: [{id,name,color}] } }
  const [bookmarkMetaByMessageId, setBookmarkMetaByMessageId] = useState({});
  const [bookmarkGroupsById, setBookmarkGroupsById] = useState({});
  const [bookmarkLabelsById, setBookmarkLabelsById] = useState({});
  const [feedbackEnabled, setFeedbackEnabled] = useState(false);

  // Vault hook
  const {
    vaultModalVisible, setVaultModalVisible, vaultModalMode, vaultPinLoading,
    vaultHasBiometric, biometricCapable, sidebarDefaultTab, setSidebarDefaultTab,
    handleVaultRequest, handleVaultPinVerify, handleVaultPinSetup,
    handleVaultBiometricAuth, handleVaultBiometricEnroll,
  } = useVault();

  useEffect(() => {
    const onVaultRequest = async (e) => {
      const result = await handleVaultRequest(e);
      if (result?.biometricSuccess) {
        setIsSidebarOpen(false);
        setSidebarDefaultTab('private');
        setTimeout(() => setIsSidebarOpen(true), 400);
      } else if (!result?.biometricSuccess && e.detail?.mode === 'verify') {
        // PIN modal opened — close sidebar first
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('vault-request', onVaultRequest);
    return () => window.removeEventListener('vault-request', onVaultRequest);
  }, [handleVaultRequest, setSidebarDefaultTab]);

  const { tier, messageUsage, refreshMessageUsage, isPaid, hasFeature, refreshStatus } = useSubscription();

  // Device fingerprint for guest tracking
  const fingerprintRef = useRef(null);
  useEffect(() => { getDeviceFingerprint().then(fp => { fingerprintRef.current = fp; }); }, []);
  
  // Check if message limit is exhausted
  const isMessageLimitExhausted = !isPaid && tier === 'free' && messageUsage.limit > 0 && messageUsage.remaining <= 0;

  // Refresh subscription status when chat page mounts (handles post-login)
  useEffect(() => {
    refreshStatus();
    // Check if feedback is enabled
    axios.get(`${API}/feature-flags/user-feedback`).then(res => setFeedbackEnabled(res.data.enabled)).catch(() => {});
    // Load bookmarks + their organisation metadata in one parallel
    // burst so the inline chip row can render immediately on mount.
    Promise.all([
      axios.get(`${API}/bookmarks`, { withCredentials: true }).catch(() => null),
      axios.get(`${API}/bookmark-groups`, { withCredentials: true }).catch(() => null),
      axios.get(`${API}/bookmark-labels`, { withCredentials: true }).catch(() => null),
    ]).then(([bmsRes, groupsRes, labelsRes]) => {
      const bookmarks = bmsRes?.data?.bookmarks || [];
      const groups = groupsRes?.data?.groups || [];
      const labels = labelsRes?.data?.labels || [];
      const groupMap = Object.fromEntries(groups.map((g) => [g.group_id, g]));
      const labelMap = Object.fromEntries(labels.map((l) => [l.label_id, l]));
      setBookmarkGroupsById(groupMap);
      setBookmarkLabelsById(labelMap);
      const meta = {};
      const ids = new Set();
      for (const bm of bookmarks) {
        if (!bm.message_id) continue;
        ids.add(bm.message_id);
        meta[bm.message_id] = {
          bookmark_id: bm.bookmark_id,
          group_id: bm.group_id || null,
          group_name: bm.group_id ? (groupMap[bm.group_id]?.name || null) : null,
          label_ids: bm.label_ids || [],
          labels: (bm.label_ids || []).map((id) => labelMap[id]).filter(Boolean),
        };
      }
      setBookmarkedIds(ids);
      setBookmarkMetaByMessageId(meta);
    });
    // Prefetch recap topics in background (warms cache so recap page loads instantly)
    const prefetchTimer = setTimeout(() => {
      axios.get(`${API}/recap/topics`, { withCredentials: true }).catch(() => {});
    }, 3000);
    return () => { clearTimeout(prefetchTimer); };
  }, [refreshStatus]);

  // Despia detection for platform-specific behavior
  const isDespiaMobile = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('despia');

  const handleToggleBibleReader = (val) => {
    const next = typeof val === 'function' ? val(showBibleReader) : val;
    if (next) { setShowSettingsPanel(false); setSourcesDrawerOpen(false); }
    setShowBibleReader(next);
  };

  const handleToggleSettingsPanel = (val) => {
    const next = typeof val === 'function' ? val(showSettingsPanel) : val;
    if (next) { setShowBibleReader(false); setSourcesDrawerOpen(false); }
    setShowSettingsPanel(next);
  };

  // Refs to track current panel state
  const bibleReaderRef = useRef(showBibleReader);
  const settingsPanelRef = useRef(showSettingsPanel);
  const sidebarOpenRef = useRef(isSidebarOpen);
  
  useEffect(() => {
    bibleReaderRef.current = showBibleReader;
  }, [showBibleReader]);
  
  useEffect(() => {
    settingsPanelRef.current = showSettingsPanel;
  }, [showSettingsPanel]);

  useEffect(() => {
    sidebarOpenRef.current = isSidebarOpen;
  }, [isSidebarOpen]);

  // Panels (Bible Reader, Settings) close via their own UI close buttons.
  // No history manipulation needed — keeps browser back navigation clean.
  
  const toggleGospelLens = async () => {
    try {
        const newValue = !userSettings?.gospel_lens_enabled;
        setUserSettings(prev => ({ ...prev, gospel_lens_enabled: newValue }));
        await axios.put(`${API}/settings`, { gospel_lens_enabled: newValue }, { withCredentials: true });
        toast.success(newValue ? 'Gospel Lens Enabled' : 'Gospel Lens Disabled');
    } catch (error) {
        setUserSettings(prev => ({ ...prev, gospel_lens_enabled: !prev.gospel_lens_enabled }));
        toast.error(t('generated.chatpage_failed_to_update_742f'));
    }
  };

  const toggleStudyTool = async (key, enabled) => {
    const prev = userSettings?.[key];
    setUserSettings(s => ({ ...s, [key]: enabled }));
    try {
      await axios.put(`${API}/settings`, { [key]: enabled }, { withCredentials: true });
    } catch {
      setUserSettings(s => ({ ...s, [key]: prev }));
      toast.error(t('generated.chatpage_failed_to_update_94e6'));
    }
  };
  const scrollViewportRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);

  const handleScroll = () => {
    if (scrollViewportRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollViewportRef.current;
        const isAtTop = scrollTop < 50;
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
        
        // Show scroll to top if we have scrolled down a bit
        setShowScrollTop(!isAtTop);
        // Show scroll to bottom if we are not at the bottom
        setShowScrollBottom(!isAtBottom);
    }
  };

  const scrollToTop = () => {
    scrollViewportRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToBottomManual = () => {
    scrollViewportRef.current?.scrollTo({ top: scrollViewportRef.current.scrollHeight, behavior: 'smooth' });
  };

  const handleScriptureClick = (refString) => {
    // Open the unified ScripturePopup (parity with Daily Reflections + DYK).
    // The popup itself offers a "Read Full Chapter" CTA that navigates
    // into the Bible Reader — so this single entry point covers both
    // a quick verse peek and a deep dive.
    setScripturePopupRef(refString);
  };

  const handleOpenChapterFromPopup = (refString) => {
    setScripturePopupRef(null);
    try {
      const parts = refString.match(/((?:(?:1|2|3)\s?)?[A-Za-z\u00C0-\u024F]+(?: (?:de(?:s|l)?|of|dos|das|the)?\s?[A-Za-z\u00C0-\u024F]+)*)\s+(\d+)(?::([\d\-\.,]+))?/);
      if (parts) {
        const rawBook = parts[1].trim();
        const book = resolveToEnglishBookName(rawBook) || rawBook;
        const chapter = parts[2];
        const verse = parts[3] || '1';
        setBibleTarget({ book, chapter, verse });
        setShowBibleReader(true);
      }
    } catch (e) {
      console.error("Failed to parse scripture reference:", refString);
    }
  };

  const handleCopy = (msg) => {
    navigator.clipboard.writeText(msg.content);
    setCopiedMessageId(msg.message_id);
    toast.success(t('chat.copied'));
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  const handleFeedback = async (msg, rating) => {
    try {
      await axios.post(`${API}/feedback`, {
        message_id: msg.message_id,
        conversation_id: currentConversation?.conversation_id,
        rating,
      }, { withCredentials: true });
      setFeedbackMap(prev => ({ ...prev, [msg.message_id]: rating }));
    } catch { /* ignore */ }
  };

  // Bookmark add/remove + organisation now lives inline on each chat
  // bubble via BookmarkOrganizePopover — the popover talks to the API
  // directly and notifies via onBookmarkAdded / onBookmarkRemoved.

  const handleShare = (msg) => {
    if (msg.content) {
      setShareModalData({ message: msg });
    }
  };


  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputMessage]);

  const scrollToBottom = (instant = false) => {
    if (scrollViewportRef.current) {
        scrollViewportRef.current.scrollTo({
            top: scrollViewportRef.current.scrollHeight,
            behavior: instant ? 'instant' : 'smooth'
        });
    }
  };

  const isStreamingRef = useRef(false);
  const justFinishedStreamingRef = useRef(false);

  useEffect(() => {
    // During streaming, don't auto-scroll — user reads from top of the response
    if (isStreamingRef.current) return;
    // After streaming just ended, skip auto-scroll so user can keep reading
    if (justFinishedStreamingRef.current) {
      justFinishedStreamingRef.current = false;
      handleScroll();
      return;
    }
    // If navigating to a specific message (e.g. from bookmark), scroll to it
    if (scrollToMessageRef.current) {
      const targetId = scrollToMessageRef.current;
      scrollToMessageRef.current = null;
      // Use setTimeout to ensure DOM has rendered after messages state update
      setTimeout(() => {
        const el = document.querySelector(`[data-message-id="${targetId}"]`);
        const viewport = scrollViewportRef.current;
        if (el && viewport) {
          // Calculate position relative to the scroll viewport
          const elRect = el.getBoundingClientRect();
          const vpRect = viewport.getBoundingClientRect();
          const scrollOffset = viewport.scrollTop + (elRect.top - vpRect.top) - 20;
          viewport.scrollTo({ top: Math.max(0, scrollOffset), behavior: 'smooth' });
          // Brief highlight
          el.style.transition = 'background 0.3s';
          el.style.background = 'var(--theme-accent-muted, rgba(99,102,241,0.1))';
          setTimeout(() => { el.style.background = ''; }, 2000);
        }
        handleScroll();
      }, 150);
      return;
    }
    scrollToBottom();
    handleScroll();
  }, [messages]);

  // ── Background-stream recovery ─────────────────────────────────────
  // Mobile webviews (iOS/Despia, Android TWA) suspend JS + kill the SSE
  // fetch reader when the app is backgrounded. The backend keeps
  // generating via `asyncio.create_task` — so the full assistant reply is
  // saved to DB. The recovery poll (started from the catch block) runs
  // continuously until the reply is found. Here we just piggyback on
  // visibility→visible to trigger ONE immediate extra poll attempt so the
  // user doesn't have to wait the full 3s tick when they return.
  useEffect(() => {
    const onHidden = () => {
      if (loading) {
        streamInterruptedRef.current = true;
        // Proactively abort the in-flight fetch. iOS/Despia webviews will
        // freeze the reader anyway, then deliver a (misleading) empty
        // `done` or STREAM_TIMEOUT when the app returns — which looks
        // like a generation failure. By aborting now we force the catch
        // block to run with AbortError while streamInterruptedRef is
        // true, which triggers the recovery poll immediately.
        try {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
        } catch (_) {}
      }
    };
    const onVisible = () => {
      if (!recoveringStreamRef.current) return;
      const cfg = recoveryPollConfigRef.current;
      if (!cfg) return;
      // Accelerate the next poll tick: cancel the pending timer and
      // restart the poll with the ORIGINAL startedAt preserved so the
      // 45s cap still fires at the right absolute time.
      if (recoveryPollTimerRef.current) {
        clearTimeout(recoveryPollTimerRef.current);
        recoveryPollTimerRef.current = null;
      }
      startRecoveryPoll(
        cfg.convId,
        cfg.tempMsgId,
        cfg.userMsg,
        cfg.baselineAssistantCount,
        cfg.startedAt,
      );
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') onVisible();
      else onHidden();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', onVisible);
    window.addEventListener('pagehide', onHidden);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', onVisible);
      window.removeEventListener('pagehide', onHidden);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, currentConversation?.conversation_id]);

  useEffect(() => {
    loadConversations();
    loadUserSettings();
    // Listen for vault conversation moves
    const handleVaultMove = () => loadConversations();
    // Listen for pin toggles
    const handlePinToggle = (e) => {
      const { conversation_id, is_pinned } = e.detail;
      setConversations(prev => {
        const updated = prev.map(c =>
          c.conversation_id === conversation_id
            ? { ...c, is_pinned, pinned_at: is_pinned ? new Date().toISOString() : null }
            : c
        );
        // Re-sort: pinned first (by pinned_at), then rest by updated_at desc
        const pinned = updated.filter(c => c.is_pinned).sort((a, b) => new Date(b.pinned_at || 0) - new Date(a.pinned_at || 0));
        const unpinned = updated.filter(c => !c.is_pinned).sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
        return [...pinned, ...unpinned];
      });
    };
    window.addEventListener('vault-conversation-moved', handleVaultMove);
    window.addEventListener('conversation-pin-toggled', handlePinToggle);
    // Rename event — optimistically patch local state so the sidebar row
    // updates instantly without a full refetch.
    const handleRename = (e) => {
      const { conversation_id, title } = e.detail || {};
      if (!conversation_id || !title) return;
      setConversations((prev) =>
        prev.map((c) => (c.conversation_id === conversation_id ? { ...c, title } : c))
      );
      setCurrentConversation((prev) =>
        prev && prev.conversation_id === conversation_id ? { ...prev, title } : prev
      );
    };
    window.addEventListener('conversation-renamed', handleRename);
    return () => {
      window.removeEventListener('vault-conversation-moved', handleVaultMove);
      window.removeEventListener('conversation-pin-toggled', handlePinToggle);
      window.removeEventListener('conversation-renamed', handleRename);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle bookmark navigation - Removed as bookmarks are gone
  useEffect(() => {
    // Legacy logic removed
  }, []);

  // Handle "Discuss with Eili" from Bible Reader
  useEffect(() => {
    if (!appReady || !discussScriptureRef.current) return;
    const scripture = discussScriptureRef.current;
    discussScriptureRef.current = null;
    // Clear navigation state so refresh doesn't re-trigger
    window.history.replaceState({}, '', '/chat');

    const initDiscussion = async () => {
      // Create a new conversation for the discussion
      try {
        const response = await axios.post(`${API}/conversations`, { is_private: false }, { withCredentials: true });
        const newConv = response.data;
        setConversations(prev => [newConv, ...prev]);
        setCurrentConversation(newConv);
        setMessages([{
          role: 'assistant',
          content: t('chat.placeholder'),
          timestamp: new Date().toISOString()
        }]);
        // Pre-fill the message and auto-send after a short delay
        const discussMessage = `Please help me understand and discuss this scripture:\n\n${scripture}`;
        setTimeout(() => sendMessage(discussMessage), 500);
      } catch {
        // Fallback: just prefill the input
        setInputMessage(`Please help me understand and discuss this scripture:\n\n${scripture}`);
      }
    };
    initDiscussion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appReady]);

  // Handle "Discuss with Eili" from Did You Know (and any other feature
  // that wants to seed a new conversation with a pre-filled topic).
  // We DO NOT auto-send — the user lands in a fresh chat with the topic
  // pre-filled in the input box, so they can ask whatever they want
  // (edit it, replace it, or send as-is).
  useEffect(() => {
    if (!appReady || !seedMessageRef.current) return;
    const seed = seedMessageRef.current;
    seedMessageRef.current = null;
    window.history.replaceState({}, '', '/chat');
    (async () => {
      try {
        const response = await axios.post(`${API}/conversations`, { is_private: false }, { withCredentials: true });
        const newConv = response.data;
        setConversations(prev => [newConv, ...prev]);
        setMessages([{
          role: 'assistant',
          content: t('chat.placeholder'),
          timestamp: new Date().toISOString()
        }]);
        pendingPrefillRef.current = seed;
        setCurrentConversation(newConv);
      } catch {
        // Even if conversation creation fails, still pre-fill the input
        // so the user can paste it into any existing conversation.
        setInputMessage(seed);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appReady]);

  // Watcher: once the new conversation commits, fill the input with the
  // seed topic. Avoids stale-closure issues that the older setTimeout
  // pattern had with currentConversation.
  useEffect(() => {
    if (!currentConversation || !pendingPrefillRef.current) return;
    const seed = pendingPrefillRef.current;
    pendingPrefillRef.current = null;
    if (autoSendSeedRef.current) {
      autoSendSeedRef.current = false;
      sendMessage(seed);
      return;
    }
    setInputMessage(seed);
    // Surface the suggested questions (if any) above the input.
    if (Array.isArray(suggestedQuestionsRef.current) && suggestedQuestionsRef.current.length) {
      setSuggestedQuestions(suggestedQuestionsRef.current);
      suggestedQuestionsRef.current = null;
    }
    // Focus the textarea after the render commits so the user can edit
    // the topic or just hit send.
    Promise.resolve().then(() => {
      try {
        const ta = document.querySelector('[data-testid="chat-message-input"]') || document.querySelector('textarea');
        if (ta && typeof ta.focus === 'function') {
          ta.focus();
          // Place caret at end so editing is natural.
          const len = ta.value?.length || 0;
          if (typeof ta.setSelectionRange === 'function') ta.setSelectionRange(len, len);
        }
      } catch { /* ignore */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversation]);

  // Clear suggested questions once the user sends a message.
  useEffect(() => {
    if (suggestedQuestions.length === 0) return;
    // Find the first user message in current messages; if it exists,
    // remove the chips.
    const hasUserMessage = messages.some(m => m.role === 'user');
    if (hasUserMessage) setSuggestedQuestions([]);
  }, [messages, suggestedQuestions.length]);

  const loadUserSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, { withCredentials: true });
      setUserSettings(response.data);
      // Show recap schedule modal for non-guest users who haven't set preferences
      if (!user?.is_guest && !response.data.recap_day) {
        setShowRecapSchedule(true);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`, { withCredentials: true });
      setConversations(response.data);
      
      // Check for deep link conversation parameter
      const params = new URLSearchParams(location.search);
      const deepLinkConvId = params.get('conversation');
      const deepLinkMsgId = params.get('message');
      const seedText = params.get('seed');

      if (deepLinkConvId) {
        // If the deep-link came from a push notification, prime the
        // scroll-to-message ref so the existing post-load effect highlights
        // the target after messages render.
        if (deepLinkMsgId) {
          scrollToMessageRef.current = deepLinkMsgId;
        }
        loadConversation(deepLinkConvId, true);
        window.history.replaceState({}, '', '/chat');
      } else if (seedText) {
        // Pre-populate input from a seeded link. Route through the
        // same `seedMessageRef` flow the other "Discuss with Eili"
        // entry points use — it creates a new conversation, prefills
        // the input, and focuses the textarea. The old shortcut that
        // just called `setInputMessage(seedText)` was silently
        // invisible when the user had no conversation selected,
        // because the chat panel replaces the textarea with an
        // empty-state placeholder in that case.
        seedMessageRef.current = seedText;
        window.history.replaceState({}, '', '/chat');
      } else if (response.data.length > 0 && !currentConversation) {
        // Open the most recently active conversation (not necessarily pinned)
        // Backend returns: pinned first, then unpinned by updated_at desc
        // Find the conversation with the most recent updated_at across all
        const mostRecent = [...response.data].sort((a, b) => 
          new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
        )[0];
        loadConversation(mostRecent.conversation_id, true);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setAppReady(true);
    }
  };

  // ── Recovery polling for background-interrupted streams ──────────────
  // When a mobile webview freezes the fetch reader mid-stream, the backend
  // keeps generating (via asyncio.create_task) and saves the reply to the
  // DB when it's done. This poll checks every few seconds until the reply
  // lands, then swaps the temp placeholder for the canonical server copy.
  // If the reply doesn't arrive within the window, we show the normal
  // "try again" retry UI instead of leaving the user staring at a blank
  // chat. Cancelled on successful stream completion or new user message.
  const cancelRecoveryPoll = () => {
    if (recoveryPollTimerRef.current) {
      clearTimeout(recoveryPollTimerRef.current);
      recoveryPollTimerRef.current = null;
    }
    recoveryPollConfigRef.current = null;
    recoveringStreamRef.current = false;
  };

  const startRecoveryPoll = (convId, tempMsgId, userMsg, baselineAssistantCount = 0, startedAt = Date.now()) => {
    if (!convId) return;
    // Persist this config so the visibility handler can accelerate the
    // next poll tick without losing context.
    recoveryPollConfigRef.current = { convId, tempMsgId, userMsg, baselineAssistantCount, startedAt };
    if (recoveryPollTimerRef.current) {
      clearTimeout(recoveryPollTimerRef.current);
      recoveryPollTimerRef.current = null;
    }
    // Give up after 165 seconds — matches the worst-case backend
    // deadline (150s for deep-study with all toggles on) plus a 15s
    // buffer for network jitter + post-processing. If the backend
    // hasn't saved a reply by then, something genuinely failed
    // server-side and we should surface the retry UI. Lighter queries
    // will resolve in 3-60s so most users never come near this cap.
    if (Date.now() - startedAt > 165 * 1000) {
      setMessages(prev => prev.map(m =>
        m.message_id === tempMsgId
          ? { ...m, timedOut: true, retryContent: userMsg }
          : m
      ));
      setLoading(false);
      isProcessing.current = false;
      recoveringStreamRef.current = false;
      return;
    }

    recoveryPollTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/conversations/${convId}`, { withCredentials: true });
        const dbMsgs = res.data?.messages || [];
        // Count assistant messages with non-empty content in the DB. If
        // this is GREATER than the baseline captured when the user sent
        // their message, the backend has saved a fresh reply for this
        // turn. We can't rely on a simple "last assistant message has
        // content" check because every conversation starts with an
        // assistant greeting message that's always non-empty — that would
        // give a false positive on poll tick #1.
        const dbAssistantWithContent = dbMsgs.filter(
          m => m.role === 'assistant' && (m.content || '').trim().length > 0
        );
        if (dbAssistantWithContent.length > baselineAssistantCount) {
          // Reply has landed — replace messages with the canonical
          // server-persisted version. Reuse loadConversation's parsing so
          // reply-context and sources render identically to a fresh load.
          const parsed = dbMsgs.map(m => {
            if (m.role === 'user' && m.content && m.content.startsWith('[Replying to: "')) {
              const endIdx = m.content.indexOf('"]\n\n');
              if (endIdx > 15) {
                const replyExcerpt = m.content.substring(15, endIdx);
                const cleanContent = m.content.substring(endIdx + 4);
                return { ...m, content: cleanContent, replyTo: { content: replyExcerpt } };
              }
            }
            return m;
          });
          setMessages(parsed);
          const loadedSources = {};
          for (const m of dbMsgs) {
            if (m.role === 'assistant' && m.sources && m.sources.length > 0) {
              loadedSources[m.message_id] = m.sources;
            }
          }
          if (Object.keys(loadedSources).length > 0) {
            setMessageSources(prev => ({ ...prev, ...loadedSources }));
          }
          setLoading(false);
          isProcessing.current = false;
          recoveringStreamRef.current = false;
          return;
        }
      } catch {
        // Ignore transient errors; the next poll will retry.
      }
      // Still no reply — keep polling.
      startRecoveryPoll(convId, tempMsgId, userMsg, baselineAssistantCount, startedAt);
    }, 1500);
  };

  const loadConversation = async (conversationId, skipCheck = false, scrollToMessageId = null) => {
    // Prevent loading if conversation is not in any list (regular or vault)
    if (!skipCheck && conversations.length > 0 && !conversations.find(c => c.conversation_id === conversationId)) {
      return;
    }

    // Store target message to scroll to after messages render
    scrollToMessageRef.current = scrollToMessageId || null;

    try {
      const response = await axios.get(`${API}/conversations/${conversationId}`, { withCredentials: true });
      setCurrentConversation(response.data.conversation);
      // Parse reply context from stored messages so the UI shows styled quote blocks
      const parsed = (response.data.messages || []).map(m => {
        if (m.role === 'user' && m.content && m.content.startsWith('[Replying to: "')) {
          const endIdx = m.content.indexOf('"]\n\n');
          if (endIdx > 15) {
            const replyExcerpt = m.content.substring(15, endIdx);
            const cleanContent = m.content.substring(endIdx + 4);
            return { ...m, content: cleanContent, replyTo: { content: replyExcerpt } };
          }
        }
        return m;
      });
      setMessages(parsed);
      // Extract stored sources from messages for the source chips
      const loadedSources = {};
      for (const m of response.data.messages || []) {
        if (m.role === 'assistant' && m.sources && m.sources.length > 0) {
          loadedSources[m.message_id] = m.sources;
        }
      }
      if (Object.keys(loadedSources).length > 0) {
        setMessageSources(prev => ({ ...prev, ...loadedSources }));
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
      if (error.response && error.response.status !== 404) {
          toast.error(t('chat.failed_load_conv'));
      }
    }
  };

  const createNewConversation = async (isPrivate = false) => {
    if (creatingConversation) return;
    setCreatingConversation(true);

    const maxRetries = 3;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await axios.post(`${API}/conversations`, { is_private: isPrivate }, { withCredentials: true });
        const newConv = response.data;
        if (isPrivate) {
          window.dispatchEvent(new CustomEvent('vault-conversation-created', { detail: newConv }));
        } else {
          setConversations([newConv, ...conversations]);
        }
        setCurrentConversation(newConv);
        setMessages([{
          role: 'assistant',
          content: t('chat.placeholder'),
          timestamp: new Date().toISOString()
        }]);
        toast.success(t('chat.new_created'));
        setCreatingConversation(false);
        return;
      } catch (error) {
        if (attempt < maxRetries - 1) {
          await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
        } else {
          toast.error(t('chat.failed_create_conv'));
        }
      }
    }
    setCreatingConversation(false);
  };

  const deleteConversation = async (conversationId) => {
    // Optimistic update
    const previousConversations = conversations;
    setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
    
    if (currentConversation?.conversation_id === conversationId) {
      setCurrentConversation(null);
      setMessages([]);
    }

    try {
      await axios.delete(`${API}/conversations/${conversationId}`, { withCredentials: true });
      toast.success(t('chat.conversation_deleted'));
    } catch (error) {
      // Revert if failed
      setConversations(previousConversations);
      toast.error(t('chat.failed_delete_conv'));
    }
  };

  const deleteAllConversations = async () => {
    try {
      await axios.delete(`${API}/conversations`, { withCredentials: true });
      setConversations([]);
      setCurrentConversation(null);
      setMessages([]);
      toast.success(t('chat.all_deleted'));
    } catch (error) {
      console.error('Failed to delete all conversations:', error);
      toast.error(t('chat.failed_delete_all'));
    }
  };

  // Swipe to Open Sidebar Logic — disabled on Despia (conflicts with iOS back gesture)
  useEffect(() => {
    if (isDespiaMobile) return; // On Despia, sidebar opens only via hamburger menu tap

    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    };

    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;
      
      // Only trigger if starting from the left edge (first 30px) to avoid conflict with horizontal scrolling
      if (touchStartX < 30) {
          const diffX = touchEndX - touchStartX;
          const diffY = Math.abs(touchEndY - touchStartY);
          
          // Check for horizontal swipe right (> 50px) with minimal vertical movement
          if (diffX > 50 && diffY < 50) {
              setIsSidebarOpen(true);
          }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDespiaMobile]);

  // Sidebar closes via its own Sheet UI or hamburger toggle.
  // No history manipulation — keeps browser back navigation clean.

  // Reset conversation function removed


  const sendMessage = async (overrideMessage) => {
    const messageToSend = overrideMessage || inputMessage.trim();
    if (isProcessing.current || !messageToSend || !currentConversation) return;
    
    // Reset interrupt/recovery state for this fresh stream and cancel any
    // poll left over from a previous interrupted generation.
    streamInterruptedRef.current = false;
    cancelRecoveryPoll();

    isProcessing.current = true;
    const userMsg = messageToSend;
    const replyCtx = replyToMessage;
    setInputMessage('');
    setReplyToMessage(null);
    setLoading(true);

    // Build the content to send to API (with reply context if replying)
    let apiContent = userMsg;
    if (replyCtx) {
      const excerpt = replyCtx.content.length > 300 ? replyCtx.content.substring(0, 300) + '...' : replyCtx.content;
      apiContent = `[Replying to: "${excerpt}"]\n\n${userMsg}`;
    }
    
    const tempUserMsg = {
      role: 'user',
      content: userMsg,
      timestamp: new Date().toISOString(),
      replyTo: replyCtx ? { content: replyCtx.content.substring(0, 150) } : undefined
    };
    
    // Add user message immediately
    setMessages(prev => [...prev, tempUserMsg]);
    
    // Add placeholder for assistant message
    const tempAiMsgId = 'temp-ai-' + Date.now();
    const tempAiMsg = {
        message_id: tempAiMsgId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempAiMsg]);

    // Scroll once (instant, no animation) to show the AI response starting,
    // then block all auto-scrolling during streaming so user can read from top
    isStreamingRef.current = true;
    setTimeout(() => scrollToBottom(true), 100);

    try {
      abortControllerRef.current = new AbortController();
      const response = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(fingerprintRef.current ? { 'X-Device-Fingerprint': fingerprintRef.current } : {}),
        },
        body: JSON.stringify({
            conversation_id: currentConversation.conversation_id,
            content: apiContent
        }),
        credentials: 'include',
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        if (response.status === 429) {
          const errData = await response.json();
          const detail = errData.detail || {};
          toast.error(detail.message || t('chat.limit_reached', 'Daily message limit reached. Upgrade for unlimited access.'));
          setShowUpgradePrompt(detail.is_guest ? 'guest_limit' : 'daily_limit');
          // Remove the placeholder messages
          setMessages(prev => prev.filter(m => m.message_id !== tempAiMsgId && m !== tempUserMsg));
          setLoading(false);
          isProcessing.current = false;
          refreshMessageUsage();
          return;
        }
        throw new Error('Network response was not ok');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let aiContent = '';
      let realMessageId = null;
      let pendingSources = [];
      let buffer = '';

      // Helper: read with per-chunk timeout (45s) to detect stalled streams
      const STREAM_TIMEOUT_MS = 45000;
      const readWithTimeout = (rdr, ms) => new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('STREAM_TIMEOUT')), ms);
        rdr.read().then(result => { clearTimeout(timer); resolve(result); })
                  .catch(err => { clearTimeout(timer); reject(err); });
      });

      while (true) {
        const { done, value } = await readWithTimeout(reader, STREAM_TIMEOUT_MS);
        if (done) {
          // Process any remaining data in buffer before breaking
          if (buffer.trim()) {
            const finalEvents = buffer.split('\n\n');
            for (const event of finalEvents) {
              if (!event.trim()) continue;
              const lines = event.split('\n');
              for (const line of lines) {
                if (!line.startsWith('data: ')) continue;
                const jsonStr = line.slice(6);
                if (!jsonStr.trim()) continue;
                try {
                  const data = JSON.parse(jsonStr);
                  if (data.type === 'content') {
                    aiContent += data.content;
                  } else if (data.type === 'meta' && data.message_id) {
                    realMessageId = data.message_id;
                  } else if (data.type === 'sources' && data.sources?.length > 0) {
                    pendingSources = data.sources;
                  } else if (data.type === 'replace') {
                    aiContent = data.content;
                  }
                } catch (e) {
                  // Ignore parse errors on final buffer
                }
              }
            }
          }
          break;
        }
        
        buffer += decoder.decode(value, { stream: true });
        // SSE format: "data: {...}\n\n" - split on double newlines
        const events = buffer.split('\n\n');
        buffer = events.pop(); // Keep the last incomplete chunk
        
        for (const event of events) {
            if (!event.trim()) continue;
            // Parse SSE format: lines starting with "data: "
            const lines = event.split('\n');
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              const jsonStr = line.slice(6); // Remove "data: " prefix
              if (!jsonStr.trim()) continue;
              try {
                const data = JSON.parse(jsonStr);
                if (data.type === 'heartbeat') {
                    continue; // Keepalive — skip
                } else if (data.type === 'meta') {
                    realMessageId = data.message_id;
                    if (data.title) {
                        setConversations(prev => prev.map(c => 
                            c.conversation_id === data.conversation_id 
                                ? { ...c, title: data.title }
                                : c
                        ));
                        if (currentConversation && currentConversation.conversation_id === data.conversation_id) {
                            setCurrentConversation(prev => ({ ...prev, title: data.title }));
                        }
                    }
                } else if (data.type === 'content') {
                    aiContent += data.content;
                    // Update state with new content
                    setMessages(prev => prev.map(msg => 
                        msg.message_id === tempAiMsgId 
                            ? { ...msg, content: aiContent }
                            : msg
                    ));
                } else if (data.type === 'replace') {
                    // Verse verification correction — replace full AI response
                    aiContent = data.content;
                    setMessages(prev => prev.map(msg => 
                        msg.message_id === tempAiMsgId 
                            ? { ...msg, content: aiContent }
                            : msg
                    ));
                } else if (data.type === 'sources') {
                    // Historical source citations for the drawer
                    if (data.sources && data.sources.length > 0) {
                      pendingSources = data.sources;
                    }
                }
              } catch (e) {
                console.error('Error parsing SSE event', e, jsonStr);
              }
            }
        }
      }

      // Final update to set the real message ID for bookmarks/notes
      isStreamingRef.current = false;
      justFinishedStreamingRef.current = true;

      // Detect empty response (stream completed but no content received)
      if (!aiContent.trim()) {
        // If the stream was interrupted by backgrounding, the webview
        // often resumes a paused fetch and delivers `done` with no
        // buffered content because the subscriber was disconnected
        // server-side while suspended. The backend task is still saving
        // the real reply — switch to recovery polling instead of
        // blaming the user with a retry UI.
        const pageHidden = typeof document !== 'undefined' && document.visibilityState !== 'visible';
        if (streamInterruptedRef.current || pageHidden) {
          recoveringStreamRef.current = true;
          const convId = currentConversation?.conversation_id;
          const baseline = messages.filter(
            m => m.role === 'assistant' &&
                 (m.content || '').trim().length > 0 &&
                 m.message_id !== tempAiMsgId
          ).length;
          startRecoveryPoll(convId, tempAiMsgId, userMsg, baseline);
          return;
        }
        setMessages(prev => prev.map(m =>
          m.message_id === tempAiMsgId
            ? { ...m, content: '', timedOut: true, retryContent: userMsg }
            : m
        ));
        setLoading(false);
        isProcessing.current = false;
        return;
      }

      if (realMessageId) {
         setMessages(prev => prev.map(msg => 
            msg.message_id === tempAiMsgId 
                ? { ...msg, message_id: realMessageId }
                : msg
        ));
        // Store sources for this message
        if (pendingSources.length > 0) {
          setMessageSources(prev => ({ ...prev, [realMessageId]: pendingSources }));
        }
      } else if (pendingSources.length > 0) {
        // Fallback: store with temp ID if real ID wasn't received
        setMessageSources(prev => ({ ...prev, [tempAiMsgId]: pendingSources }));
      }

      // Move active conversation to top of list (update its updated_at)
      const nowISO = new Date().toISOString();
      setConversations(prev => {
        const updated = prev.map(c =>
          c.conversation_id === currentConversation?.conversation_id
            ? { ...c, updated_at: nowISO }
            : c
        );
        const pinned = updated.filter(c => c.is_pinned).sort((a, b) => new Date(b.pinned_at || 0) - new Date(a.pinned_at || 0));
        const unpinned = updated.filter(c => !c.is_pinned).sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
        return [...pinned, ...unpinned];
      });

    } catch (error) {
      isStreamingRef.current = false;
      justFinishedStreamingRef.current = true;

      // Helper: switch into recovery-poll mode instead of showing the
      // retry UI. Used whenever we detect the stream was interrupted by
      // the mobile OS (backgrounding) rather than a genuine server error.
      const triggerRecovery = () => {
        recoveringStreamRef.current = true;
        const convId = currentConversation?.conversation_id;
        const baseline = messages.filter(
          m => m.role === 'assistant' &&
               (m.content || '').trim().length > 0 &&
               m.message_id !== tempAiMsgId
        ).length;
        startRecoveryPoll(convId, tempAiMsgId, userMsg, baseline);
      };

      const pageHidden = typeof document !== 'undefined' && document.visibilityState !== 'visible';
      const wasInterrupted = streamInterruptedRef.current || pageHidden;

      if (error.name === 'AbortError') {
        // If the abort was triggered by our own visibility handler
        // (backgrounding), fall into recovery — the backend is still
        // generating. If it was a genuine user "Stop" click,
        // streamInterruptedRef would be false — keep partial content.
        if (wasInterrupted) {
          triggerRecovery();
          return;
        }
        return;
      }
      if (error.message === 'STREAM_TIMEOUT') {
        // A 45s gap without any chunk. On mobile webviews this is almost
        // always caused by the OS pausing the fetch reader while the app
        // was backgrounded — treat it as a recovery scenario if we know
        // the stream was interrupted. Otherwise fall through to the
        // normal "stalled" retry UI.
        if (wasInterrupted) {
          triggerRecovery();
          return;
        }
        setMessages(prev => prev.map(m =>
          m.message_id === tempAiMsgId ? { ...m, timedOut: true, retryContent: userMsg } : m
        ));
        return;
      }
      // Generic error. If the page was hidden (or was hidden at any
      // point during this stream), the backend task keeps running and
      // will save the full reply to the DB — start the recovery poll.
      if (wasInterrupted) {
        triggerRecovery();
        return;
      }
      console.error(error);
      // Show retry for failed messages (with or without partial content)
      setMessages(prev => prev.map(m =>
        m.message_id === tempAiMsgId ? { ...m, timedOut: true, retryContent: userMsg } : m
      ));
    } finally {
      // Keep loading=true while recovering so the thinking indicator stays
      // visible. It's cleared either inside startRecoveryPoll (on success
      // or final timeout) or on next user action.
      if (!recoveringStreamRef.current) {
        setLoading(false);
        isProcessing.current = false;
      }
      abortControllerRef.current = null;
      refreshMessageUsage();
    }
  };

  const retryMessage = (retryContent) => {
    // Remove the failed/timed-out message and resend
    setMessages(prev => prev.filter(m => !m.error && !m.timedOut));
    sendMessage(retryContent);
  };

  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setLoading(false);
    isProcessing.current = false;
    toast.info(t('chat.generation_stopped'));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (isProcessing.current || !file || !currentConversation) return;
    
    if (!hasFeature('file_upload')) {
      setShowUpgradePrompt('file_upload');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    isProcessing.current = true;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('conversation_id', currentConversation.conversation_id);
    
    setFileUploading(true);
    try {
      await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        params: { conversation_id: currentConversation.conversation_id }
      });
      toast.success(`${file.name} ${t('chat.file_uploaded')}`);
      // Reload conversation to show the assistant's prompt
      await loadConversation(currentConversation.conversation_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('chat.failed_upload'));
    } finally {
      setFileUploading(false);
      isProcessing.current = false;
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const analyzeYouTube = async () => {
    if (isProcessing.current || !youtubeUrl.trim() || !currentConversation) return;
    
    if (!hasFeature('youtube_analysis')) {
      setShowUpgradePrompt('youtube_analysis');
      return;
    }
    
    isProcessing.current = true;
    setYoutubeAnalyzing(true);
    try {
      const response = await axios.post(
        `${API}/youtube/analyze`,
        {
          conversation_id: currentConversation.conversation_id,
          youtube_url: youtubeUrl
        },
        { withCredentials: true }
      );
      
      toast.success(t('chat.video_loaded'));
      setYoutubeUrl('');
      setYoutubeDialogOpen(false);
      
      // If there's an analysis message (error or non-religious), show it.
      // If it was successful (religious), the backend added a prompt message, so just reload.
      if (response.data.analysis && response.data.analysis.includes(t('chat.video_loaded'))) {
         await loadConversation(currentConversation.conversation_id);
      } else {
         // Fallback for non-religious or error messages returned directly
         const analysisMsg = {
            role: 'assistant',
            content: response.data.analysis || "Analysis complete.",
            timestamp: new Date().toISOString()
         };
         setMessages([...messages, analysisMsg]);
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || t('chat.failed_analyze'));
    } finally {
      setYoutubeAnalyzing(false);
      isProcessing.current = false;
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      // Purge cached per-user data so the next user on this device
      // never sees a stale flash of the previous session's Home.
      try {
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith('home_cache_') || k.startsWith('eili_cache_v1:')) {
            localStorage.removeItem(k);
          }
        });
      } catch { /* private mode / storage disabled */ }
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const handleRecapScheduleConfirm = async ({ day, time, timezone }) => {
    try {
      await axios.put(`${API}/settings`, {
        recap_day: day,
        recap_time: time,
        recap_timezone: timezone
      }, { withCredentials: true });
      setUserSettings(prev => ({ ...prev, recap_day: day, recap_time: time, recap_timezone: timezone }));
      toast.success(t('recap_schedule.saved', 'Weekly recap scheduled!'));
    } catch (error) {
      console.error('Failed to save recap schedule:', error);
    }
    setShowRecapSchedule(false);
  };

  const handleRecapScheduleSkip = async () => {
    // Set a sentinel value so modal doesn't show again
    try {
      await axios.put(`${API}/settings`, { recap_day: 'skipped' }, { withCredentials: true });
      setUserSettings(prev => ({ ...prev, recap_day: 'skipped' }));
    } catch {}
    setShowRecapSchedule(false);
  };

  return (
    <div className="h-dvh theme-bg flex flex-col overflow-hidden despia-chat-root">
      <Header 
        user={user} 
        showBibleReader={showBibleReader} 
        setShowBibleReader={handleToggleBibleReader} 
        showSettingsPanel={showSettingsPanel}
        setShowSettingsPanel={handleToggleSettingsPanel}
        onNewChat={() => createNewConversation()}
        onMobileMenuClick={() => setIsSidebarOpen(true)}
        desktopSidebarOpen={desktopSidebarOpen}
        onToggleDesktopSidebar={() => setDesktopSidebarOpen(prev => !prev)}
      />
      {/* Mobile Sidebar Sheet */}
      <Sheet open={isSidebarOpen} onOpenChange={(open) => {
        // On Despia, only allow closing via Sheet (not opening via swipe)
        if (!open) setIsSidebarOpen(false);
        // Opening is handled only by hamburger menu tap
        if (open && !isDespiaMobile) setIsSidebarOpen(true);
      }}>
        <SheetContent side="left" hideClose={true} className="p-0 w-80 border-r border-border theme-surface">
            <ConversationSidebar
              conversations={conversations}
              currentConversationId={currentConversation?.conversation_id}
              onLoadConversation={(id, skipCheck, msgId) => {
                loadConversation(id, skipCheck, msgId);
                setIsSidebarOpen(false);
              }}
              onCreateNewConversation={(isPrivate) => {
                createNewConversation(isPrivate);
                setIsSidebarOpen(false);
              }}
              onDeleteConversation={deleteConversation}
              onDeleteAllConversations={deleteAllConversations}
              onBookmarkRemoved={(messageId) => setBookmarkedIds(prev => { const n = new Set(prev); n.delete(messageId); return n; })}
              bookmarkCount={bookmarkedIds.size}
              closeSidebar={() => setIsSidebarOpen(false)}
              defaultTab={sidebarDefaultTab}
              onTabChanged={(tab) => setSidebarDefaultTab(tab)}
              chatReady={appReady && !creatingConversation}
              isGuest={isGuest}
              onGuestBlock={(feature) => setGuestPrompt(feature)}
            />
        </SheetContent>
      </Sheet>
      {/* Vault PIN Modal — rendered outside Sheet to avoid Radix focus trap */}
      {vaultModalVisible && (
        <VaultPinModal
          mode={vaultModalMode}
          onVerify={handleVaultPinVerify}
          onSetup={handleVaultPinSetup}
          onClose={() => setVaultModalVisible(false)}
          loading={vaultPinLoading}
          hasBiometric={vaultHasBiometric}
          onBiometricAuth={handleVaultBiometricAuth}
          onBiometricEnroll={handleVaultBiometricEnroll}
          biometricAvailable={biometricCapable}
        />
      )}
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Sidebar - Conversations (Desktop).
            Reveal breakpoint intentionally bumped to `lg:` (1024px) so
            on iPad Stage Manager / Split View (which typically produces
            640-900px window widths), the desktop sidebar hides and the
            mobile Sheet drawer above takes over. Previously used `md:`
            (768px) which left ~640-768px windows in an awkward state
            where the 320px sidebar squished the main chat area. */}
        <div 
          className={`shrink-0 theme-surface border-r border-border hidden lg:block h-full transition-all duration-300 overflow-hidden ${desktopSidebarOpen ? 'w-80 xl:w-96' : 'w-0 border-r-0'}`}
        >
          <div className="w-80 xl:w-96 h-full">
           <ConversationSidebar
              conversations={conversations}
              currentConversationId={currentConversation?.conversation_id}
              onLoadConversation={loadConversation}
              onCreateNewConversation={createNewConversation}
              onDeleteConversation={deleteConversation}
              onDeleteAllConversations={deleteAllConversations}
              onBookmarkRemoved={(messageId) => setBookmarkedIds(prev => { const n = new Set(prev); n.delete(messageId); return n; })}
              bookmarkCount={bookmarkedIds.size}
              defaultTab={sidebarDefaultTab}
              onTabChanged={(tab) => setSidebarDefaultTab(tab)}
              chatReady={appReady && !creatingConversation}
              isGuest={isGuest}
              onGuestBlock={(feature) => setGuestPrompt(feature)}
            />
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 min-w-0 flex flex-col relative overflow-hidden">
          {currentConversation ? (
            <>
              {/* Messages */}
              <ScrollArea 
                viewportRef={scrollViewportRef}
                onScroll={handleScroll}
                className="flex-1 px-4 md:px-6 lg:px-8 py-8 pb-48"
              >
                <div className="max-w-3xl lg:max-w-4xl mx-auto">
                  <BroadcastChatMessages />
                  {messages.map((msg, idx) => {
                    const msgDate = msg.timestamp ? new Date(msg.timestamp) : null;
                    const prevDate = idx > 0 && messages[idx - 1].timestamp ? new Date(messages[idx - 1].timestamp) : null;
                    const showDateSep = msgDate && (!prevDate || msgDate.toDateString() !== prevDate.toDateString());
                    const timeStr = msgDate ? msgDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';

                    // Last assistant message gets clickable follow-up chips.
                    const isLastAssistant =
                      msg.role === 'assistant' &&
                      idx === messages.length - 1;

                    return (
                    <div key={idx}>
                      {showDateSep && (
                        <div className="flex items-center justify-center my-6" data-testid={`date-sep-${idx}`}>
                          <span className="text-[10px] tracking-widest uppercase px-3 py-1 rounded-full" style={{ color: 'var(--theme-text-secondary)', background: 'var(--theme-hover)', opacity: 0.7 }}>
                            {msgDate.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      )}
                      <div
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} mb-8`}
                        data-testid={`message-${msg.role}-${idx}`}
                        data-message-id={msg.message_id}
                      >
                        <div className="flex flex-col" style={{ maxWidth: '85%' }}>
                          <ChatMessageBubble
                            msg={msg}
                            idx={idx}
                            loading={loading}
                            copiedMessageId={copiedMessageId}
                            setCopiedMessageId={setCopiedMessageId}
                            feedbackMap={feedbackMap}
                            feedbackEnabled={feedbackEnabled}
                            bookmarkedIds={bookmarkedIds}
                            bookmarkMeta={bookmarkMetaByMessageId[msg.message_id]}
                            bookmarkGroupsById={bookmarkGroupsById}
                            bookmarkLabelsById={bookmarkLabelsById}
                            isGuest={isGuest}
                            sources={messageSources[msg.message_id]}
                            onCopy={handleCopy}
                            onFeedback={handleFeedback}
                            onBookmarkAdded={(messageId, meta) => {
                              setBookmarkedIds(prev => new Set([...prev, messageId]));
                              if (meta) {
                                setBookmarkMetaByMessageId(prev => ({
                                  ...prev,
                                  [messageId]: {
                                    bookmark_id: meta.bookmark_id,
                                    group_id: null,
                                    group_name: null,
                                    label_ids: [],
                                    labels: [],
                                  },
                                }));
                              }
                            }}
                            onBookmarkRemoved={(messageId) => {
                              setBookmarkedIds(prev => { const n = new Set(prev); n.delete(messageId); return n; });
                              setBookmarkMetaByMessageId(prev => {
                                const next = { ...prev };
                                delete next[messageId];
                                return next;
                              });
                            }}
                            onBookmarkMetaChange={(messageId, partial) => {
                              setBookmarkMetaByMessageId(prev => {
                                const cur = prev[messageId] || {};
                                const merged = { ...cur, ...partial };
                                // Recompute label objects + group name from id-to-object maps
                                if (partial.label_ids) {
                                  merged.labels = partial.label_ids.map((id) => bookmarkLabelsById[id]).filter(Boolean);
                                }
                                if ('group_id' in partial) {
                                  merged.group_name = partial.group_id ? (bookmarkGroupsById[partial.group_id]?.name || null) : null;
                                }
                                return { ...prev, [messageId]: merged };
                              });
                            }}
                            onGroupCreated={(group) => {
                              setBookmarkGroupsById(prev => ({ ...prev, [group.group_id]: group }));
                            }}
                            onLabelCreated={(label) => {
                              setBookmarkLabelsById(prev => ({ ...prev, [label.label_id]: label }));
                            }}
                            conversationId={currentConversation?.conversation_id}
                            onShare={handleShare}
                            onShareToFriend={communityShareEnabled ? (m) => setShareToFriendData({ message: m }) : null}
                            onPostToFeed={communityShareEnabled ? (m) => setPostToFeedData({ message: m }) : null}
                            onReply={(m) => setReplyToMessage({ message_id: m.message_id, content: m.content })}
                            onRetry={retryMessage}
                            onScriptureClick={handleScriptureClick}
                            onGuestBlock={setGuestPrompt}
                            onSourceChipClick={(msgId, chipIdx) => {
                              setDrawerSources(messageSources[msgId] || []);
                              setDrawerActiveIndex(chipIdx);
                              setSourcesDrawerOpen(true);
                              setShowBibleReader(false);
                              setShowSettingsPanel(false);
                            }}
                            onFollowupSelect={isLastAssistant ? ((q) => sendMessage(q)) : undefined}
                            isStreaming={loading}
                            textareaRef={textareaRef}
                          />
                          {timeStr && (
                            <span className={`text-[10px] mt-1 px-2 ${msg.role === 'user' ? 'self-end' : 'self-start'}`} style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }} data-testid={`msg-time-${idx}`}>
                              {timeStr}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                  {/* Starter Questions — shown when no user messages yet */}
                  {!loading && messages.filter(m => m.role === 'user').length === 0 && (
                    <StarterQuestions onSelect={(q) => sendMessage(q)} />
                  )}
                  <div ref={messagesEndRef} className="pb-16" />
                </div>
              </ScrollArea>
              
              {/* Scroll To Top - Top Center of Response Area */}
              {showScrollTop && !showBibleReader && !showSettingsPanel && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 pointer-events-auto">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => scrollViewportRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="h-8 w-8 rounded-full bg-background/60 backdrop-blur-md border border-border/50 text-muted-foreground hover:bg-background/90 hover:text-foreground transition-all shadow-sm"
                    title={t('chat.scroll_top')}
                    aria-label={t('chat.scroll_top')}
                  >
                    <ChevronUp className="h-5 w-5" />
                  </Button>
                </div>
              )}

              {/* Floating Input Area */}
              <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-col items-center px-4 pointer-events-none chat-input-area" style={{ paddingBottom: '0.5rem', paddingLeft: 'max(1rem, var(--sal))', paddingRight: 'max(1rem, var(--sar))' }}>
                {/* Scroll To Bottom */}
                {showScrollBottom && !showBibleReader && !showSettingsPanel && (
                  <div className="pointer-events-auto mb-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => scrollViewportRef.current?.scrollTo({ top: scrollViewportRef.current.scrollHeight, behavior: 'smooth' })}
                      className="h-8 w-8 rounded-full bg-background/60 backdrop-blur-md border border-border/50 text-muted-foreground hover:bg-background/90 hover:text-foreground transition-all shadow-sm"
                      title={t('chat.scroll_bottom')}
                      aria-label={t('chat.scroll_bottom')}
                      data-testid="scroll-to-bottom-btn"
                    >
                      <ChevronDown className="h-5 w-5" />
                    </Button>
                  </div>
                )}
                {/* Stop Generation - floating above input */}
                {loading && (
                  <div className="pointer-events-auto mb-3">
                    <Button
                      onClick={stopGeneration}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 border-destructive/30 text-destructive hover:bg-destructive/10 rounded-full px-5 py-2 shadow-lg theme-surface backdrop-blur-md animate-in fade-in slide-in-from-bottom-2 duration-200"
                      data-testid="stop-generation-button"
                    >
                      <StopCircle className="h-4 w-4" />
                      {t('chat.stop')}
                    </Button>
                  </div>
                )}
                {/* Premium Feature Highlight for Free Users.
                    `mx-auto` centers the highlight within the chat
                    input tray so it aligns with the message column
                    above — critical at iPad Stage Manager widths
                    where the tray sits inside a wider container and
                    would otherwise float left. */}
                {!isPaid && tier === 'free' && (
                  <div className="pointer-events-auto w-full max-w-3xl lg:max-w-4xl mx-auto mb-2">
                    <PremiumHighlight onUpgrade={(feature) => setShowUpgradePrompt(feature)} />
                  </div>
                )}
                {/* Chat input tray — `mx-auto` mirrors the alignment
                    of the message column so the input never floats
                    left of the messages when the window is wider than
                    the max-width cap (e.g., iPad Stage Manager splits
                    where 700-900px window > 768px content cap). */}
                <div className="w-full max-w-3xl lg:max-w-4xl mx-auto pointer-events-auto">
                  <div className="bg-card/95 backdrop-blur-xl border border-border p-3 md:p-4 rounded-[2.5rem] shadow-2xl transition-all duration-200">
                    <ChatToolbar
                      currentConversation={currentConversation}
                      userSettings={userSettings}
                      isGuest={isGuest}
                      isPaid={isPaid}
                      fileInputRef={fileInputRef}
                      fileUploading={fileUploading}
                      youtubeUrl={youtubeUrl}
                      setYoutubeUrl={setYoutubeUrl}
                      youtubeAnalyzing={youtubeAnalyzing}
                      hasFeature={hasFeature}
                      onCreateNewConversation={createNewConversation}
                      onFileUpload={handleFileUpload}
                      onAnalyzeYouTube={analyzeYouTube}
                      onToggleStudyTool={toggleStudyTool}
                      onDeleteConversation={deleteConversation}
                      onToggleSettings={() => handleToggleSettingsPanel(prev => !prev)}
                      onShowUpgrade={setShowUpgradePrompt}
                    />
                  
                  <BroadcastBanner />
                  
                  {!isPaid && tier === 'free' && messageUsage.limit > 0 && (
                    <div className="mb-2 px-1">
                      <MessageLimitBanner used={messageUsage.used || 0} limit={messageUsage.limit} isGuest={!!user?.is_guest} />
                    </div>
                  )}
                  {isMessageLimitExhausted ? (
                    <div 
                      className="flex gap-2 items-center justify-center bg-card border border-border rounded-[2rem] p-4 opacity-60"
                      style={{ background: 'rgba(100,100,100,0.1)' }}
                    >
                      <span className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
                        {user?.is_guest
                          ? t('chat.guest_limit_placeholder', 'Sign up for a free account to continue chatting')
                          : t('chat.limit_reached_placeholder', 'Daily message limit reached')}
                      </span>
                    </div>
                  ) : (
                  <>
                  {replyToMessage && (
                    <div
                      className="flex items-start gap-3 mb-2 px-4 py-3 rounded-2xl border theme-border animate-in slide-in-from-bottom-2 duration-200"
                      style={{ background: 'var(--theme-hover)' }}
                      data-testid="reply-quote-bar"
                    >
                      <div className="w-0.5 self-stretch rounded-full flex-shrink-0" style={{ background: 'var(--theme-accent)' }} />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium block mb-0.5" style={{ color: 'var(--theme-accent)' }}>{t('generated.chatpage_replying_to_eili_1ed8')}</span>
                        <p className="text-sm theme-text-secondary line-clamp-2 leading-relaxed">
                          {replyToMessage.content.substring(0, 150)}{replyToMessage.content.length > 150 ? '...' : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => setReplyToMessage(null)}
                        className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                        data-testid="reply-quote-dismiss"
                      >
                        <X className="h-3.5 w-3.5 theme-text-secondary" />
                      </button>
                    </div>
                  )}
                  {suggestedQuestions.length > 0 && (
                    <div
                      className="mb-3 flex flex-wrap gap-2"
                      data-testid="dyk-suggested-questions"
                    >
                      <div className="w-full flex items-center justify-between mb-1">
                        <span
                          className="text-[11px] uppercase tracking-wider font-medium"
                          style={{ color: 'var(--theme-textSecondary)' }}
                        >
                          {t('generated.chatpage_suggested_follow_ups_77e9')}
                        </span>
                        <button
                          onClick={() => setSuggestedQuestions([])}
                          className="text-xs px-2 py-0.5 rounded-full hover:bg-white/5"
                          style={{ color: 'var(--theme-textSecondary)' }}
                          data-testid="dyk-suggested-dismiss"
                        >
                          {t('generated.chatpage_dismiss_08bf')}
                        </button>
                      </div>
                      {suggestedQuestions.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            setInputMessage(q);
                            try {
                              const ta = textareaRef.current;
                              if (ta && typeof ta.focus === 'function') {
                                ta.focus();
                                const len = q.length;
                                if (typeof ta.setSelectionRange === 'function') {
                                  ta.setSelectionRange(len, len);
                                }
                              }
                            } catch { /* ignore */ }
                          }}
                          className="text-sm px-3 py-2 rounded-full transition-all hover:scale-[1.03] active:scale-[0.97] border"
                          style={{
                            background: 'var(--glass-bg-active)',
                            borderColor: 'var(--glass-border)',
                            color: 'var(--theme-text)',
                          }}
                          data-testid={`dyk-suggested-q-${i}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 items-end bg-card border border-border rounded-[2rem] p-2 pl-4 shadow-inner focus-within:ring-2 focus-within:ring-ring focus-within:ring-opacity-50 transition-all">
                    <Textarea
                      ref={textareaRef}
                      placeholder={t('chat.placeholder')}
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      // Enter now inserts a newline (default textarea
                      // behaviour). Sending is exclusively via the Send
                      // button — matches modern chat-UI conventions like
                      // WhatsApp on web and gives users a frictionless
                      // way to compose multi-line questions.
                      className="flex-1 resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent py-3 px-0 min-h-[44px] theme-text placeholder:text-muted-foreground text-base"
                      rows={1}
                      style={{ maxHeight: '200px' }}
                      data-testid="message-input"
                    />
                    <Button
                      onClick={() => sendMessage()}
                      disabled={loading || !inputMessage.trim()}
                      className="bg-[var(--theme-accent)] hover:opacity-90 rounded-full h-10 w-10 p-0 mb-1 mr-1 shadow-md transition-transform active:scale-95"
                      style={{ color: 'var(--theme-surface)' }}
                      data-testid="send-message-button"
                      aria-label={t('generated.chatpage_send_message_0863')}
                    >
                      <Send className="h-5 w-5" />
                    </Button>
                  </div>
                  </>
                  )}
                </div>
              </div>
            </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <img src="/logo.png" alt={t('generated.chatpage_bible_a818')} className="h-16 w-16 mx-auto mb-4 opacity-30 object-contain cursor-pointer" onClick={() => createNewConversation()} />
                <h2 className="text-2xl font-medium theme-text mb-2">{t('chat.no_conversation')}</h2>
                <p className="theme-text-secondary mb-6">{t('chat.start_prompt')}</p>
                <Button
                  onClick={() => createNewConversation()}
                  disabled={creatingConversation || !appReady}
                  className="bg-[var(--theme-accent)] hover:opacity-90"
                  style={{ color: 'var(--theme-surface)' }}
                  data-testid="create-first-conversation-button"
                >
                  {creatingConversation ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : !appReady ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('chat.new_conversation')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
        {/* Bible Reader Panel */}
        {showBibleReader && (
          <div className="absolute inset-0 z-30 border-l theme-border theme-surface shadow-xl lg:shadow-none lg:static lg:w-auto">
            <BibleReader
              onClose={() => setShowBibleReader(false)}
              userTranslations={userSettings?.selected_translations || ['asv']}
              targetReference={bibleTarget}
              onDiscuss={(scriptureBlock) => {
                setShowBibleReader(false);
                const discussMessage = `Please help me understand and discuss this scripture:\n\n${scriptureBlock}`;
                sendMessage(discussMessage);
              }}
            />
          </div>
        )}
        {/* Settings Panel */}
        {showSettingsPanel && (
          <div className="absolute top-0 right-0 h-full w-full lg:static lg:w-auto z-30 border-l theme-border theme-surface shadow-xl lg:shadow-none">
            <SettingsPage
              user={user}
              asPanel={true}
              onClose={() => setShowSettingsPanel(false)}
            />
          </div>
        )}
        {/* Sources Drawer */}
        {sourcesDrawerOpen && drawerSources.length > 0 && (
          <SourcesDrawer
            sources={drawerSources}
            activeIndex={drawerActiveIndex}
            onClose={() => setSourcesDrawerOpen(false)}
          />
        )}
      </div>
      {/* Recap Schedule Modal */}
      {showRecapSchedule && (
        <RecapScheduleModal
          onConfirm={handleRecapScheduleConfirm}
          onSkip={handleRecapScheduleSkip}
        />
      )}
      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <UpgradePrompt feature={showUpgradePrompt} onClose={() => setShowUpgradePrompt(null)} />
      )}
      {/* Share Image Modal */}
      {shareModalData && (
        <ShareModal
          message={shareModalData.message}
          onClose={() => setShareModalData(null)}
        />
      )}
      {/* Share to Friend Modal */}
      {shareToFriendData && (
        <ShareToFriendModal
          message={shareToFriendData.message}
          conversationTitle={currentConversation?.title}
          onClose={() => setShareToFriendData(null)}
        />
      )}
      {/* Post to Feed Modal */}
      {postToFeedData && (
        <PostToFeedModal
          message={postToFeedData.message}
          conversationTitle={currentConversation?.title}
          onClose={() => setPostToFeedData(null)}
        />
      )}
      {showTutorial && localStorage.getItem('eili_terms_accepted') && (
        <TutorialOverlay onComplete={() => setShowTutorial(false)} />
      )}
      {/* Guest Feature Prompt */}
      <AlertDialog open={!!guestPrompt} onOpenChange={(open) => { if (!open) setGuestPrompt(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('generated.chatpage_create_a_free_0d42')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('generated.chatpage_sign_up_for_c8de')} {guestPrompt}{t('generated.chatpage_it_only_takes_8c5c')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="guest-prompt-dismiss">{t('generated.chatpage_not_now_72b7')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setGuestPrompt(null); navigate('/signup'); }} data-testid="guest-prompt-signup" className="bg-[var(--theme-accent)]" style={{ color: '#fff' }}>
              {t('generated.chatpage_create_free_account_fc1c')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Bookmark add/remove + group/label organisation now happens
          inline via BookmarkOrganizePopover on each chat bubble — no
          dedicated remove-confirmation dialog needed. */}
      {/* Universal Scripture Popup — shown when the user taps any
          scripture reference (chip in appendix OR inline in body). */}
      {scripturePopupRef && (
        <ScripturePopup
          reference={scripturePopupRef}
          onClose={() => setScripturePopupRef(null)}
          onReadChapter={handleOpenChapterFromPopup}
        />
      )}
    </div>
  );
}

